'use client'

import { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHiveIconSmallLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom'

import { useSlideshow } from './SlideshowContext'

const copyrightYear = new Date().getFullYear()
const FALLBACK_IMAGE = '/images/footer-1.jpg'

export default function HomeFooterBanner() {
    const context = useSlideshow()
    const currentSlide = context?.currentSlide

    const [activeIndex, setActiveIndex] = useState(0)
    const [images, setImages] = useState([
        { src: FALLBACK_IMAGE, alt: 'hive modern floral imagery' }
    ])
    // Mobile shows one random image per page load: latch the first slide the
    // context delivers and ignore every later update (the hidden desktop
    // carousel keeps firing setCurrentSlide on a 28s autoplay even below md)
    const [mobileImage, setMobileImage] = useState(null)

    useEffect(() => {
        const newSrc = currentSlide?.footerImage?.src || FALLBACK_IMAGE
        const newAlt = currentSlide?.footerImage?.alt || 'hive modern floral imagery'

        if (currentSlide) {
            setMobileImage(prev => prev || { src: newSrc, alt: newAlt })
        }

        // Find if this image already exists
        const existingIndex = images.findIndex(img => img.src === newSrc)

        if (existingIndex >= 0) {
            // Image exists, just switch to it
            setActiveIndex(existingIndex)
        } else {
            // Add new image and switch to it
            setImages(prev => [...prev, { src: newSrc, alt: newAlt }])
            setActiveIndex(images.length)
        }
    }, [currentSlide])

    return (
        <div className="relative overflow-hidden">
            {/* Mobile: the single latched image, static for the life of the page */}
            <div className="md:hidden">
                {mobileImage ? (
                    <Image
                        src={mobileImage.src}
                        alt={mobileImage.alt}
                        width={1800}
                        height={300}
                        quality={90}
                        sizes="100vw"
                        className="w-full h-auto"
                    />
                ) : (
                    <div className="w-full aspect-[6/1]" />
                )}
            </div>
            {/* Desktop: crossfading stack driven by the carousel */}
            <div className="hidden md:block">
                {images.map((image, index) => (
                    <Image
                        key={image.src}
                        src={image.src}
                        alt={image.alt}
                        width={1800}
                        height={300}
                        quality={90}
                        sizes="100vw"
                        className={`w-full h-auto transition-opacity duration-500 ease-out ${
                            index === activeIndex ? 'opacity-100' : 'opacity-0'
                        } ${index === 0 ? '' : 'absolute top-0 left-0'}`}
                    />
                ))}
            </div>
            <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="text-center">
                    <FontAwesomeIcon
                        icon={faHiveIconSmallLight}
                        className="inline text-primary text-4xl mb-1"
                        aria-hidden="true"
                    />
                    <div className="text-white text-md">Modern Design for the Home</div>
                </div>
            </div>
            <div className="absolute bottom-2 right-2 text-white z-10 text-right text-4xs">
                &copy; 2002-{copyrightYear} hive all rights reserved
            </div>
        </div>
    )
}
